<template>
  <template v-if="!loading">
    <vl-grid v-vl-spacer:bottom.small class="section">
      <vl-column width="8" width-xs="12">
        <vl-title tag-name="h3"> {{ $t('entitylink.detail.title') }} </vl-title>
      </vl-column>
      <vl-column v-if="editStore.canEdit" width="4" width-xs="12">
        <vl-action-group mod-align-right>
          <vl-button
            v-if="canAddEntityLink"
            icon="plus"
            :mod-disabled="isAddDisabled"
            mod-icon-before
            @click="editStore.openCreate(ProductProcessDefinitionEditType.ProductEntityLink)"
            >{{ $t('general.add') }}</vl-button
          >
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <vl-grid :class="{ edit: editStore.isCreate(ProductProcessDefinitionEditType.ProductEntityLink) }">
      <vl-column>
        <ProductEntityLinkCreate
          v-if="editStore.isCreate(ProductProcessDefinitionEditType.ProductEntityLink)"
          :specification-id="id"
        />
      </vl-column>
    </vl-grid>
    <vl-grid v-vl-spacer:bottom.small class="section">
      <vl-column width="12" width-xs="12">
        <vl-accordion-list v-if="items && items.length">
          <vl-accordion-list-item v-for="(link, i) in items" :id="i" :key="i">
            <vl-accordion :id="i.toString()" :title="getLabel(link)">
              <vl-grid
                :class="{
                  edit: editStore.isEdit(ProductProcessDefinitionEditType.ProductEntityLink, link.id)
                }"
              >
                <vl-column width="8">
                  <ProductEntityLinkEdit
                    v-if="editStore.isEdit(ProductProcessDefinitionEditType.ProductEntityLink, link.id)"
                    ref="editForm"
                    :specification-id="id"
                    :detail="link"
                  />
                  <ProductEntityLinkRead v-else :detail="link" />
                </vl-column>
                <vl-column v-if="editStore.canEdit" width="4">
                  <vl-action-group
                    v-if="
                      items &&
                      items.length &&
                      !editStore.isEdit(ProductProcessDefinitionEditType.ProductEntityLink, link.id)
                    "
                    mod-align-right
                    mod-space
                    between
                  >
                    <vl-button
                      icon="pencil"
                      mod-icon-before
                      mod-secondary
                      @click="editStore.openEdit(ProductProcessDefinitionEditType.ProductEntityLink, link.id)"
                      >{{ $t('general.edit') }}</vl-button
                    >
                    <vl-button icon="trash" mod-icon-before mod-error @click="showDeleteModal(link.id)">{{
                      $t('general.delete')
                    }}</vl-button>
                  </vl-action-group>
                  <vl-action-group v-else mod-align-right mod-space between>
                    <vl-button icon="save" x mod-icon-before @click="save">{{ $t('general.save') }}</vl-button>
                    <vl-button icon="cross" mod-icon-before mod-secondary mod-error @click="editStore.closeEdit">
                      {{ $t('general.cancel') }}
                    </vl-button>
                  </vl-action-group>
                </vl-column>
                <template v-if="!editStore.isEdit(ProductProcessDefinitionEditType.ProductEntityLink, link.id)">
                  <vl-column width="10">
                    <vl-title tag-name="h4"> {{ $t('entitylink.detail.attributes') }} </vl-title>
                  </vl-column>
                  <ProductEntityLinkAttributes :detail="link" :specification-id="id" />
                </template>
              </vl-grid>
            </vl-accordion>
          </vl-accordion-list-item>
        </vl-accordion-list>
        <template v-else>
          <vl-annotation>{{ $t('entitylink.detail.empty') }}</vl-annotation>
        </template>
      </vl-column>
    </vl-grid>
  </template>
</template>
<script lang="ts" setup>
import { ProductEntityLinkDetailDto } from '@/api/backoffice/clients';
import { useProductEntityLinkStore } from '@/modules/configuration/store/product-entity-link/product-entity-link.store';
import { computed, onMounted, ref, Ref } from 'vue';
import ProductEntityLinkRead from './ProductEntityLinkRead.vue';
import ProductEntityLinkAttributes from './ProductEntityLinkAttributes.vue';
import {
  ProductProcessDefinitionEditType,
  useProductProcessDefinitionEditStore
} from '@/modules/configuration/store/product-process-definition/product-process-definition-edit.store';
import { useProductEntityStore } from '@/modules/configuration/store/product-entity/product-entity.store';
import ProductEntityLinkCreate from './ProductEntityLinkCreate.vue';
import ProductEntityLinkEdit from './ProductEntityLinkEdit.vue';
import { useI18n } from 'vue-i18n';
import { useModalStore } from '@/modules/core/store/modal.store';

const entityLinkStore = useProductEntityLinkStore();
const entityStore = useProductEntityStore();
const editStore = useProductProcessDefinitionEditStore();
const modalStore = useModalStore();

const { t } = useI18n();

const props = defineProps({
  id: {
    type: Number,
    required: true
  }
});

const editForm: Ref<typeof ProductEntityLinkEdit | (typeof ProductEntityLinkEdit)[]> = ref(null);

const loading = computed((): boolean => {
  return entityLinkStore.loading;
});

const items = computed((): ProductEntityLinkDetailDto[] => {
  const unsortedItems = entityLinkStore.bySpecification(props.id);
  return unsortedItems.sort((a, b) => getLabel(a).localeCompare(getLabel(b)));
});

const isAddDisabled = computed(() => {
  return (
    editStore.isEdit(ProductProcessDefinitionEditType.ProductEntityLink, props.id) ||
    editStore.isCreate(ProductProcessDefinitionEditType.ProductEntity)
  );
});

const canAddEntityLink = computed(() => {
  return entityStore.bySpecification(props.id).length >= 2;
});

onMounted(async () => {
  if (props.id) {
    await entityLinkStore.getBySpecification(props.id);
  }
});

const save = () => {
  if (Array.isArray(editForm?.value)) {
    editForm.value[0].onSubmit();
  } else {
    editForm.value.onSubmit();
  }
};

const showDeleteModal = (linkId: number) => {
  modalStore.showDeleteModal({
    title: t('entitylink.delete.title'),
    props: {
      confirm: () => entityLinkStore.deleteEntity(props.id, linkId)
    }
  });
};

const getLabel = (item: ProductEntityLinkDetailDto): string => {
  const unknown = '(?)';
  const from = `${item.fromEntity?.code ?? unknown}`;
  const to = `${item.toEntity?.code ?? unknown}`;
  return t('entitylink.detail.linkTo', { from, to });
};
</script>
<style lang="scss" scoped>
:deep(.vl-accordion__panel) {
  padding-right: 0;
}
.vl-grid {
  flex-grow: 1;
}
.section {
  padding-left: 20px;
}
</style>
